import {
  BaseElement,
  ContainerElement,
  ContainerLayout,
  containerLayout,
  ElementVisibility,
  MarketingForgeElementType,
} from "@redotech/redo-content-builder-common/element-schema";
import {
  resolveAxisSizing,
  resolveChildSizing,
} from "@redotech/redo-content-builder-common/element-sizing";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { memo, ReactNode, useMemo } from "react";
import {
  childContrastBackgroundColor,
  getBaseElementStyles,
} from "./base-element-styles";
import { SignupFormDiscountCodeDisplay } from "./discount-code";
import { SizedBoxContext } from "./element-box";
import { FormButton, shouldHideFormButton } from "./form-button";
import { FormImage } from "./form-image";
import { FormProduct } from "./form-product";
import { FormElementProps } from "./index";
import { Input } from "./input";
import { RichText } from "./rich-text";
import { TextToSubscribeButton } from "./text-to-subscribe-button";
import { TextToSubscribeDescription } from "./text-to-subscribe-description";

type ContainerStyleProps = Pick<
  BaseElement,
  "backgroundColor" | "borderColor" | "borderWidth" | "padding"
> &
  Pick<ContainerElement, "gapPx" | "growHeightWithContent">;

export const ContainerStyleWrapper = memo(function ContainerStyleWrapper({
  element,
  layout,
  children,
}: {
  element: ContainerStyleProps;
  layout: ContainerLayout;
  children: ReactNode;
}) {
  return (
    <Flex
      align={layout.alignItems}
      dir={layout.direction}
      flex="1"
      gap="none"
      justify={layout.justifyContent}
      // design-allow: merchant-authored styles and per-axis overflow have no props
      style={{
        ...getBaseElementStyles(element),
        ...(element.gapPx !== undefined && { gap: `${element.gapPx}px` }),
        // x must be `clip`, not `hidden`: a `visible` y beside a `hidden` x
        // computes to `auto`, a scroll container that can't grow.
        ...(element.growHeightWithContent
          ? {
              overflowX: "clip",
              overflowY: "visible",
              minHeight: "min-content",
            }
          : { overflow: "hidden", minHeight: 0 }),
      }}
    >
      {children}
    </Flex>
  );
});

interface ContainerChildWrapperProps {
  element: ContainerElement["elements"][number];
  direction: "row" | "column";
  /** True when mobile force-stacked a container the merchant made horizontal. */
  forceStacked: boolean;
  children: ReactNode;
}

export const ContainerChildWrapper = memo(function ContainerChildWrapper({
  element,
  direction,
  forceStacked,
  children,
}: ContainerChildWrapperProps) {
  const { flex, ...size } = resolveChildSizing(element, {
    renderedDirection: direction,
    forceStacked,
  });
  const sizedBox = useMemo(
    () => ({
      hugsHeight:
        forceStacked ||
        resolveAxisSizing(
          element,
          "height",
          direction === "row" ? "width" : "height",
        ).mode === "hug",
    }),
    [element, direction, forceStacked],
  );
  return (
    <Flex
      align="stretch"
      dir={direction}
      flex={flex}
      gap="none"
      style={size} // design-allow: merchant-authored pixel sizes have no tokens
    >
      <SizedBoxContext.Provider value={sizedBox}>
        {children}
      </SizedBoxContext.Provider>
    </Flex>
  );
});

export const FormContainer = memo(function FormContainer({
  element,
  isMobile = false,
  ...props
}: FormElementProps<ContainerElement> & { isMobile?: boolean }) {
  const layout = containerLayout(element, isMobile);
  const direction = layout.direction;
  const forceStacked = isMobile && element.direction === "horizontal";

  return (
    <ContainerStyleWrapper element={element} layout={layout}>
      {element.elements
        .filter((childElement) => {
          const visibility = childElement.visibility ?? ElementVisibility.ALL;
          if (visibility === ElementVisibility.DESKTOP_ONLY && isMobile) {
            return false;
          }
          if (visibility === ElementVisibility.MOBILE_ONLY && !isMobile) {
            return false;
          }
          // Skip close buttons that FormButton would render as null in this
          // context (no onClose wired, e.g. embedded mode). Wrapping them in
          // ContainerChildWrapper anyway leaves a 0-height phantom row.
          if (
            childElement.type === MarketingForgeElementType.BUTTON &&
            shouldHideFormButton(childElement, props.onClose, props.isBuilder)
          ) {
            return false;
          }
          return true;
        })
        .map((childElement, index) => (
          <ContainerChildWrapper
            direction={direction}
            element={childElement}
            forceStacked={forceStacked}
            key={childElement._id ?? index}
          >
            <FormContainerChild
              {...props}
              backgroundColor={childContrastBackgroundColor(
                props.backgroundColor,
                element.backgroundColor,
              )}
              element={childElement}
              isMobile={isMobile}
            />
          </ContainerChildWrapper>
        ))}
    </ContainerStyleWrapper>
  );
});

const FormContainerChild = memo(function FormContainerChild({
  element,
  isMobile,
  ...props
}: { element: ContainerElement["elements"][number]; isMobile: boolean } & Omit<
  FormElementProps<ContainerElement>,
  "element"
>) {
  const type = element.type;
  switch (type) {
    case MarketingForgeElementType.RICH_TEXT:
      return <RichText element={element} {...props} />;
    case MarketingForgeElementType.IMAGE:
      return <FormImage element={element} {...props} />;
    case MarketingForgeElementType.PRODUCT:
      return <FormProduct element={element} {...props} />;
    case MarketingForgeElementType.CONTAINER:
      return <FormContainer element={element} isMobile={isMobile} {...props} />;
    case MarketingForgeElementType.INPUT:
      return <Input element={element} {...props} />;
    case MarketingForgeElementType.BUTTON:
      return <FormButton element={element} {...props} />;
    case MarketingForgeElementType.DISCOUNT:
      return <SignupFormDiscountCodeDisplay element={element} {...props} />;
    case MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON:
      return <TextToSubscribeButton element={element} {...props} />;
    case MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION:
      return <TextToSubscribeDescription element={element} {...props} />;
    case MarketingForgeElementType.COLUMN:
      throw new Error("Column type not supported");
    default:
      assertNever(type);
  }
});
